(() => {
  if (window.KHFUIController) return;

  const KEYS = {
    seen: 'khf_exit_discount_seen',
    dismissedAt: 'khf_exit_discount_dismissed_at',
    claimedAt: 'khf_exit_discount_claimed_at',
    configuratorSuppressedUntil: 'khf_configurator_popup_suppressed_until',
    sessionStartedAt: 'khf_exit_discount_session_started_at',
    pageViews: 'khf_exit_discount_page_views',
    productScrolled: 'khf_exit_discount_product_scrolled_60'
  };
  const DAYS = 24 * 60 * 60 * 1000;
  const DESKTOP_MINIMUM_TIME = 20 * 1000;
  const MOBILE_MINIMUM_TIME = 45 * 1000;
  const TOP_EDGE_THRESHOLD = 24;
  const INTERACTION_COOLDOWN = 2500;

  const safeStorage = {
    get(storage, key) {
      try {
        return storage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(storage, key, value) {
      try {
        storage.setItem(key, value);
      } catch (_) {
        // Storage may be unavailable in strict privacy modes. The in-memory state still prevents repeats.
      }
    }
  };

  const controller = {
    ready: false,
    shownInMemory: false,
    mobileTimer: null,
    previousFocus: null,
    interactionCooldownUntil: 0,
    lastPointerY: Number.POSITIVE_INFINITY,
    lastPointerAt: 0,
    modal: null,
    panel: null,

    init() {
      if (this.ready) return;
      this.modal = document.querySelector('[data-khf-exit-discount]');
      if (!this.modal) return;
      this.ready = true;
      this.modal.dataset.ready = 'true';
      this.panel = this.modal.querySelector('[data-khf-exit-discount-panel]');
      this.ensureSessionStart();
      this.recordPageView();
      this.bind();
      this.observeProductScroll();
      this.scheduleMobileCheck();
    },

    bind() {
      document.addEventListener('mouseout', (event) => this.onDesktopExit(event));
      document.addEventListener('pointermove', (event) => this.recordPointerPosition(event), { passive: true });
      window.addEventListener('blur', () => this.onDesktopBlur());
      document.addEventListener('keydown', (event) => this.onKeydown(event));
      document.addEventListener('khf:modal:open', () => {
        this.interactionCooldownUntil = Date.now() + INTERACTION_COOLDOWN;
      });
      document.addEventListener('khf:modal:close', () => {
        this.interactionCooldownUntil = Date.now() + INTERACTION_COOLDOWN;
      });
      document.addEventListener('khf:configurator:open', () => {
        this.interactionCooldownUntil = Date.now() + INTERACTION_COOLDOWN;
      });
      document.addEventListener('khf:configurator:recommended', () => {
        const until = Date.now() + 10 * 60 * 1000;
        safeStorage.set(sessionStorage, KEYS.configuratorSuppressedUntil, String(until));
        this.interactionCooldownUntil = until;
        this.close(false);
      });
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.dismiss();
      });
      this.modal.querySelector('[data-khf-exit-discount-close]')?.addEventListener('click', () => this.dismiss());
      this.modal.querySelector('[data-khf-exit-discount-dismiss]')?.addEventListener('click', () => this.dismiss());
      this.modal.querySelector('[data-khf-exit-discount-claim]')?.addEventListener('click', () => this.claim());
    },

    ensureSessionStart() {
      if (!safeStorage.get(sessionStorage, KEYS.sessionStartedAt)) {
        safeStorage.set(sessionStorage, KEYS.sessionStartedAt, String(Date.now()));
      }
    },

    recordPageView() {
      let paths = [];
      try {
        paths = JSON.parse(safeStorage.get(sessionStorage, KEYS.pageViews) || '[]');
      } catch (_) {
        paths = [];
      }
      const path = `${location.pathname}${location.search}`;
      if (!paths.includes(path)) paths.push(path);
      safeStorage.set(sessionStorage, KEYS.pageViews, JSON.stringify(paths.slice(-20)));
    },

    getSessionAge() {
      const startedAt = Number(safeStorage.get(sessionStorage, KEYS.sessionStartedAt)) || Date.now();
      return Date.now() - startedAt;
    },

    isMobile() {
      return window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
    },

    isEligibleBrowsingPage() {
      const template = document.querySelector('main[data-template]')?.dataset.template;
      return template === 'index' || template === 'product' || template === 'collection';
    },

    isExcludedPage() {
      const template = document.querySelector('main[data-template]')?.dataset.template || '';
      const path = location.pathname.toLowerCase();
      if (template === 'password' || template === 'customers') return true;
      if (path.startsWith('/account') || path.startsWith('/checkouts') || path.startsWith('/checkout')) return true;
      return [
        '/pages/register-warranty',
        '/pages/contact',
        '/pages/privacy-policy',
        '/pages/terms-of-service',
        '/pages/return-refund-policy',
        '/pages/refund-policy',
        '/pages/shipping-policy',
        '/policies/privacy-policy',
        '/policies/terms-of-service',
        '/policies/refund-policy',
        '/policies/shipping-policy'
      ].some((excludedPath) => path === excludedPath || path.startsWith(`${excludedPath}/`));
    },

    hasDiscountApplied() {
      const pathAndQuery = `${location.pathname}${location.search}`.toUpperCase();
      const cookies = document.cookie.toUpperCase();
      return (
        pathAndQuery.includes('/DISCOUNT/KHFSAVE') ||
        pathAndQuery.includes('DISCOUNT=KHFSAVE') ||
        cookies.includes('DISCOUNT_CODE=KHFSAVE')
      );
    },

    isFrequencySuppressed() {
      if (this.shownInMemory || safeStorage.get(sessionStorage, KEYS.seen) === 'true') return true;
      const dismissedAt = Number(safeStorage.get(localStorage, KEYS.dismissedAt)) || 0;
      const claimedAt = Number(safeStorage.get(localStorage, KEYS.claimedAt)) || 0;
      if (dismissedAt && Date.now() - dismissedAt < 7 * DAYS) return true;
      if (claimedAt && Date.now() - claimedAt < 30 * DAYS) return true;
      const configuratorUntil = Number(safeStorage.get(sessionStorage, KEYS.configuratorSuppressedUntil)) || 0;
      return Date.now() < configuratorUntil;
    },

    hasConflict() {
      if (Date.now() < this.interactionCooldownUntil) return true;
      if (document.querySelector('khf-product-finder')) return true;

      const selectors = [
        '[data-khf-authenticity-modal]:not([hidden])',
        '[data-khf-warranty-modal]:not([hidden])',
        'cart-drawer.active',
        'cart-notification.active',
        'details[open] > .search-modal',
        'details[open] > .header__submenu',
        'details[open] > .menu-drawer',
        '.menu-drawer-container[open]',
        'dialog[open]',
        'modal-dialog[open]',
        'quick-add-modal[open]',
        'product-modal[open]',
        '[role="dialog"]:not([hidden]):not([data-khf-exit-discount])',
        '[aria-modal="true"]:not([hidden]):not([data-khf-exit-discount])',
        '#shopify-pc__banner:not([hidden])',
        '.shopify-pc__banner__dialog',
        '[class*="cookie"][role="dialog"]',
        '[class*="consent"][role="dialog"]'
      ];

      return selectors.some((selector) => {
        try {
          return [...document.querySelectorAll(selector)].some((element) => this.isVisible(element));
        } catch (_) {
          return false;
        }
      });
    },

    isVisible(element) {
      if (!element || element === this.modal) return false;
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0 && rect.height > 0;
    },

    canShow() {
      return !this.isExcludedPage() && !this.hasDiscountApplied() && !this.isFrequencySuppressed() && !this.hasConflict();
    },

    onDesktopExit(event) {
      if (!this.canTriggerDesktopExit()) return;
      if (event.relatedTarget || event.toElement) return;
      if (event.clientY > TOP_EDGE_THRESHOLD) return;
      this.open();
    },

    recordPointerPosition(event) {
      if (this.isMobile()) return;
      this.lastPointerY = event.clientY;
      this.lastPointerAt = Date.now();
      // Some desktop browsers never report mouseout when the pointer moves
      // into their own tab or address-bar chrome. The top edge itself is a
      // reliable exit signal, so make the offer visible before that boundary.
      if (event.clientY <= 4 && this.canTriggerDesktopExit()) this.open();
    },

    canTriggerDesktopExit() {
      return !this.isMobile() && this.getSessionAge() >= DESKTOP_MINIMUM_TIME && this.isEligibleBrowsingPage();
    },

    onDesktopBlur() {
      if (!this.canTriggerDesktopExit()) return;
      // Browsers do not always emit mouseout when the pointer enters their own
      // tab/address-bar chrome. Treat a fresh pointer movement at the top edge
      // followed by blur as the same exit intent.
      if (Date.now() - this.lastPointerAt > 1500) return;
      if (this.lastPointerY > TOP_EDGE_THRESHOLD) return;
      this.open();
    },

    observeProductScroll() {
      const template = document.querySelector('main[data-template]')?.dataset.template;
      if (template !== 'product') return;
      const check = () => {
        const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        if (window.scrollY / scrollable >= 0.6) {
          safeStorage.set(sessionStorage, KEYS.productScrolled, 'true');
          window.removeEventListener('scroll', check);
        }
      };
      window.addEventListener('scroll', check, { passive: true });
      check();
    },

    hasMobileEngagement() {
      let paths = [];
      try {
        paths = JSON.parse(safeStorage.get(sessionStorage, KEYS.pageViews) || '[]');
      } catch (_) {
        paths = [];
      }
      return paths.length >= 2 || safeStorage.get(sessionStorage, KEYS.productScrolled) === 'true';
    },

    scheduleMobileCheck() {
      if (!this.isMobile() || this.isExcludedPage()) return;
      const check = () => {
        if (this.getSessionAge() >= MOBILE_MINIMUM_TIME && this.hasMobileEngagement()) {
          this.open();
          if (!this.modal.hidden || this.isFrequencySuppressed()) return;
        }
        this.mobileTimer = window.setTimeout(check, 1500);
      };
      const delay = Math.max(500, MOBILE_MINIMUM_TIME - this.getSessionAge());
      this.mobileTimer = window.setTimeout(check, delay);
    },

    open() {
      if (!this.canShow()) return;
      this.previousFocus = document.activeElement;
      this.shownInMemory = true;
      safeStorage.set(sessionStorage, KEYS.seen, 'true');
      this.modal.hidden = false;
      document.documentElement.classList.add('khf-exit-discount-open');
      document.body.style.overflow = 'hidden';
      this.panel?.focus();
    },

    close(restoreFocus = true) {
      if (!this.modal || this.modal.hidden) return;
      this.modal.hidden = true;
      document.documentElement.classList.remove('khf-exit-discount-open');
      document.body.style.overflow = '';
      if (restoreFocus && this.previousFocus?.focus) this.previousFocus.focus();
    },

    dismiss() {
      safeStorage.set(localStorage, KEYS.dismissedAt, String(Date.now()));
      this.close();
    },

    claim() {
      safeStorage.set(localStorage, KEYS.claimedAt, String(Date.now()));
      safeStorage.set(sessionStorage, KEYS.seen, 'true');
    },

    onKeydown(event) {
      if (!this.modal || this.modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.dismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.modal.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => this.isVisible(element));
      if (!focusable.length) {
        event.preventDefault();
        this.panel?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  window.KHFUIController = controller;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => controller.init(), { once: true });
  } else {
    controller.init();
  }
  document.addEventListener('shopify:section:load', () => controller.init());
})();
